import {Input} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {SelectItem} from 'primeng/primeng';
import {isNullOrUndefined} from 'util';
import {GenericLinkedResource} from '../../../core/types/base';
import {sortByLabelFunc} from '../../../core/utils/index';
import {GenericResourceService} from '../../../features/services/generic-resource.service';
import {ResourceHandler} from '../../../features/types';
import {DisplayTypes} from '../../../ui-base/components/panel/types';
import {LinkItem, StyleType} from '../../../ui-base/types';
import {EntityForm} from '../../../ui-models/shared/entity-form';
import {ModalService} from '../../../widgets/slide-in-modal/services/modal.service';
import {CreatePlaylistComponent} from '../components/create-playlist/create-playlist.component';
import {ModifyPlaylistComponent} from '../components/modify-playlist/modify-playlist.component';
import {hasOwnProperty} from "tslint/lib/utils";

export abstract class AvodCatalogBase extends EntityForm {
  @Input() allowCreate = false;
  isFeature = false;

  playlistItems = [];
  styleType = StyleType;
  PanelDisplayTypes = DisplayTypes;
  avodCatalogServiceOptions: SelectItem[];
  throttleOptions: SelectItem[];
  modeOptions: SelectItem[];
  currentAvodCatalogHandler: ResourceHandler;
  avodCatalog;
  service = null;
  genericService;
  modalService;

  labels = {
    name: 'Name',
    feature: 'Feature',
    avodCatalogService: 'AVoD Catalog Service',
    mode: 'Mode',
    throttle: 'Throttle',
    locationId: 'Location ID',
    lowWaterMark: 'Low Water Mark',
    triggerFile: 'Trigger File',
    baseURL: 'Base URL',
    additionalPaths: 'Additional Paths',
    rootPath: 'Root Path',
    playlistsBasePath: 'Playlist Base Path',
    removableMedia: 'Removable Media',
    trackRemoves: 'Track Removes',
    usePlaylists: 'Use Playlists',
    genre: 'Genre (Guaranteed Metadata)',
    artist: 'Artist (Guaranteed Metadata)',
    albumArtist: 'Album Artist (Guaranteed Metadata)',
    album: 'Album (Guaranteed Metadata)',
  };

  get formIsReady() {
    let isReady = !isNullOrUndefined(this.form);
    isReady = isReady && !isNullOrUndefined(this.avodCatalogServiceOptions);
    isReady = isReady && !isNullOrUndefined(this.modeOptions);
    isReady = isReady && !isNullOrUndefined(this.throttleOptions);
    return isReady;
  }

  constructor(formBuilder: FormBuilder,
              genericService: GenericResourceService,
              modalService: ModalService) {
    super(formBuilder);
    this.genericService = genericService;
    this.modalService = modalService;
  }

  modeChanged($event) {
    if ((this.form.value.mode === 'tracker') && !this.form.value.removable_media ) {
      this.form.patchValue({root_path: '/mnt/private_media', trigger_file: '.metainfo.dat'});
    } else if (this.form.value.mode === 'mpu') {
      this.form.patchValue({
        root_path: '/mnt/protected_media',
        trigger_file: 'mpu.xml',
        removable_media: false,
      });
    }
  }

  removableMediaChanged($event) {
    if (this.form.value.removable_media) {
      this.form.patchValue({
        root_path: '/mnt/usb',
        trigger_file: '.metainfo.dat',
      });
    } else {
      this.modeChanged(null);
    }
  }

  openCreateDialog() {
    const compRef = this.modalService.open(CreatePlaylistComponent);
    const onFullfill = (playlist) => {
      console.log('Created new playlist');
    };
    const onError = () => {
    };

    compRef.componentInstance.title = `New Playlist`;
    compRef.componentInstance.source = this.getModel();
    compRef.componentInstance.isFeature = this.isFeature;
    compRef.componentInstance.afterCreate
      .subscribe((playlist) => {
        this.refresh();
      });

    compRef.result.then(onFullfill, onError);

  }

  // Down from this point all methods are private

  protected afterUpdateForm(form: FormGroup) {
    if (isNullOrUndefined(this.service)) { return; }
    this.avodCatalog = this.getModel();
    this.currentAvodCatalogHandler = this.genericService.findServiceForResource(this.avodCatalog._links.self.href);
    this.currentAvodCatalogHandler.load();
    this.playlistItems = this.buildItemsFromLinkedResources(this.service.getPlaylists(this.avodCatalog));
  }

  protected updateOptionsList(items: any[]) {
    const optionList = [];
    items.forEach((item) => {
      let _label = '';
      if (hasOwnProperty(item, 'label')) {
        _label = item.label;
      } else {
        _label = item.name + '@' + item._links.unit_type.label;
      }
      optionList.push({label: _label, value: item._links.self.href});
    });
    optionList.sort(sortByLabelFunc);
    return optionList;
  }

  protected updateAvodCatalogServiceOptions(items: any[]) {
    const services = items.filter( (service) => {
      return service['name'] === 'AVODCatalog';
    });
    this.avodCatalogServiceOptions = this.updateOptionsList(services);
  }

  /**
   * Update the available GUI zone types
   *
   * @param {string[]} choices
   */
  protected updateChoicesOptions(choices: string[]) {
    const items: SelectItem[] = [];
    choices.forEach((name: string) => items.push({label: name, value: name}));
    return items;
  }

  protected buildItemsFromLinkedResources(items: GenericLinkedResource[]): LinkItem[] {
    const options: LinkItem[] = [];
    items.forEach((item: GenericLinkedResource) => options.push({
      label: item.label,
      navigate: () => {
        this.openModifyDialog(item);
      },
    }));
    return options;
  }

  protected refresh() {
    console.error('Abstract method: To be implemented by inherited class');
  }

  private openModifyDialog(resource: GenericLinkedResource) {
    let compRef;
    const onFullfill = (value: any) => {
      console.log('closed create modal');
    };

    const onError = () => {
    };

    const resourceHandler = this
      .genericService
      .findServiceForResource(resource.href);

    resourceHandler
      .getCurrent()
      .subscribe((playlist) => {
        if (compRef) {
          compRef.close();
          compRef = null;
        }

        if (isNullOrUndefined(playlist)) {
          compRef = null;
        } else {
          compRef = this.modalService.open(ModifyPlaylistComponent);
          compRef.componentInstance.model = playlist;
          compRef.componentInstance.title = `Modify Playlist`;
          compRef.componentInstance.displayDelete = true;
          compRef.componentInstance.afterUpdate.subscribe(() => this.refresh());
          compRef.componentInstance.afterDelete.subscribe(() => this.refresh());
          compRef.result.then(onFullfill, onError);
        }
      });

    if (!resourceHandler.isReady()) {
      resourceHandler.load();
    }

  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/models/avod-catalog/avod-catalog-base/avod-catalog-base.ts